<template>
  <div>
    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <div class="flex items-baseline gap-3">
        <h2 class="text-lg font-semibold text-gray-800">Entity Summary</h2>
        <span class="text-xs text-gray-500 tabular-nums">
          {{ totalRows.toLocaleString() }} {{ rowNoun }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <input
          v-model="searchQuery" data-demo-search
          type="text"
          placeholder="Search..."
          class="sensitive px-3 py-1.5 text-sm border border-gray-300 rounded-md w-48 focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white"
        />
        <ExportMenu :disabled="!tableRows.length" :exports="[{ type: 'excel', action: handleExport, loading: exporting }]" />
      </div>
    </div>

    <div class="flex flex-col gap-y-2 mb-2">
      <div class="flex items-center flex-wrap gap-2">
        <span class="text-xs font-medium text-gray-700">Group by</span>
        <button
          v-for="opt in entityChipsInline"
          :key="'entity-' + opt.value"
          type="button"
          :class="chipClass(isSelected('entity:' + opt.value))"
          @click="toggleChip('entity:' + opt.value)"
        >
          <span v-if="isSelected('entity:' + opt.value)" class="tabular-nums">{{ orderNum('entity:' + opt.value) }}. </span>{{ opt.label }}
        </button>
        <select
          v-if="hasHiddenChips"
          :value="''"
          class="px-2.5 py-1 text-xs border border-dashed border-gray-300 rounded-full bg-white text-gray-500 hover:bg-gray-50 focus:outline-none"
          @change="onAddGroupBy"
        >
          <option value="" disabled>+ Group by</option>
          <option v-for="opt in dropdownEntityChips" :key="'de-' + opt.value" :value="'entity:' + opt.value">{{ opt.label }}</option>
        </select>
      </div>
    </div>

    <div class="flex items-center flex-wrap gap-x-4 gap-y-2 mb-4">
      <div v-if="grainType" class="flex items-center flex-wrap gap-2">
        <span class="text-xs font-medium text-gray-700">Filter by</span>
        <template v-for="typeName in activeFilterTypes" :key="typeName">
          <div class="inline-flex items-center gap-1">
            <span class="text-[11px] text-gray-500">{{ typeName }}</span>
            <MultiSelectDropdown
              :modelValue="hierarchyFilters[typeName] || []"
              :options="hierarchyFilterOptions[typeName] || []"
              :sensitive-labels="true"
              :placeholder="typeName"
              :searchable="(hierarchyFilterOptions[typeName] || []).length > 20"
              compact
              @update:modelValue="updateHierarchyFilter(typeName, $event)"
            />
            <button
              class="text-gray-400 hover:text-red-500 leading-none text-base px-0.5"
              :title="`Remove ${typeName} filter`"
              @click="removeFilterType(typeName)"
            >&times;</button>
          </div>
        </template>
        <select
          v-if="availableFilterTypes.length > 0"
          :value="''"
          class="px-2.5 py-1 text-xs border border-dashed border-gray-300 rounded-full bg-white text-gray-500 hover:bg-gray-50 focus:outline-none"
          @change="onAddFilterType"
        >
          <option value="" disabled>+ Filter</option>
          <option v-for="t in availableFilterTypes" :key="t" :value="t">{{ t }}</option>
        </select>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-xs font-medium text-gray-700">Tag Type</span>
        <select
          v-model="selectedTagType"
          class="px-2.5 py-1 text-xs border border-gray-300 rounded-full bg-white text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
          @change="onTagTypeChange"
        >
          <option value="__all__">All</option>
          <option v-for="t in availableTagTypes" :key="t" :value="t">{{ t }}</option>
        </select>
      </div>
      <GroupedInfoTagFilter
        v-model="infoTagGroups"
        v-model:groupsMode="infoTagGroupsMode"
        :infoTagType="selectedTagType === '__all__' ? null : selectedTagType"
        label=""
        buttonLabel="Tag"
        color="emerald"
        direction="horizontal"
      />
      <button
        v-if="hasActiveFilters"
        class="text-[11px] text-gray-500 hover:text-gray-700 underline ml-auto"
        @click="clearFilters"
      >Clear all</button>
    </div>

    <div class="relative border border-growth-50 rounded-lg bg-white overflow-hidden">
      <div
        v-if="attrLoading && !loading"
        class="absolute inset-0 z-[2] flex items-center justify-center gap-2 bg-white/70 text-xs text-growth-500"
      >
        <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-growth-200 border-t-growth-500"></span>
        Updating…
      </div>
      <div class="max-h-[360px] overflow-y-auto">
        <table class="w-full text-xs">
          <thead class="border-b border-growth-50 text-left text-growth-600 sticky top-0 bg-white z-[1]">
            <tr>
              <th
                v-for="col in displayColumns"
                :key="col.key"
                :class="[
                  'py-2 px-3 font-medium',
                  col.sortable ? 'cursor-pointer select-none hover:text-gray-900' : '',
                  col.align === 'right' ? 'text-right' : (col.align === 'center' ? 'text-center' : '')
                ]"
                @click="col.sortable && toggleSort(col.key)"
              >
                <span
                  class="inline-flex items-center gap-1"
                  :class="col.align === 'right' ? 'justify-end' : (col.align === 'center' ? 'justify-center' : '')"
                >
                  {{ col.label }}
                  <span v-if="col.sortable" class="text-[11px]">{{ sortIndicator(col.key) }}</span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td :colspan="displayColumns.length" class="text-center py-10 text-growth-400">Loading…</td>
            </tr>
            <template v-else>
              <tr
                v-for="item in tableRows"
                :key="item.key"
                :class="['border-b border-growth-50', item.entity_id > 0 ? 'cursor-pointer hover:bg-gray-50' : '']"
                @click="goToEntity(item)"
              >
                <td class="py-3 px-4 text-growth-800">
                  <span
                    class="sensitive font-medium text-neutral-700"
                    :class="item.entity_id > 0 ? 'hover:text-neutral-900 hover:underline' : ''"
                    :data-spoof-kind="entitySpoofKind(grainType, item.name)"
                  >{{ item.name }}</span>
                </td>
                <td
                  v-for="col in columnDefs"
                  :key="col.key"
                  class="py-3 px-3 whitespace-nowrap"
                  :class="item.columns[col.key] === noneLabel ? 'text-gray-400' : 'sensitive text-gray-700'"
                  :data-spoof-kind="entitySpoofKind(col.dim, item.columns[col.key])"
                >
                  {{ item.columns[col.key] }}
                </td>
                <td class="py-3 px-2">
                  <div class="flex flex-wrap gap-1 items-center">
                    <span
                      v-for="tag in displayTags(item)"
                      :key="tag.id"
                      class="sensitive inline-flex items-center gap-1.5 text-xs leading-snug px-2.5 py-1 rounded-full bg-white border border-gray-200 text-gray-700 max-w-[200px]"
                      data-spoof-kind="label"
                      :title="tag.type ? `${tag.type}: ${tag.name}` : tag.name"
                    >
                      <span class="w-2 h-2 rounded-full shrink-0" :style="{ backgroundColor: tag.color || '#6b7280' }"></span>
                      <span class="truncate">{{ tag.name }}</span>
                    </span>
                    <span
                      v-if="tagOverflowCount(item) > 0"
                      class="text-[11px] text-growth-600 ml-1"
                      :title="overflowTagsTooltip(item)"
                    >+{{ tagOverflowCount(item) }}</span>
                  </div>
                </td>
                <td v-if="showEngagementScore" class="sensitive py-3 px-2">
                  <CC_Engagement_Score_Badge :params="{ value: item.engagement_score }" inline />
                </td>
                <td class="sensitive py-3 px-2 text-right text-growth-800 tabular-nums">
                  {{ formatNum(item.engagements) }}
                </td>
                <td class="sensitive py-3 px-2 text-right text-growth-800 tabular-nums">
                  <span v-if="attrLoading" class="text-growth-300">…</span>
                  <span v-else>{{ formatNum(item.attribution) }}</span>
                </td>
                <td class="sensitive py-3 px-2 text-right text-growth-600">
                  {{ formatDate(item.lastEngagement) }}
                </td>
              </tr>
              <tr v-if="!loading && tableRows.length === 0">
                <td :colspan="displayColumns.length" class="text-center py-10 text-growth-400">No entities found</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <EntityReporting
      v-if="grainType"
      class="mt-4 border border-gray-200 rounded-lg bg-white"
      :allEntities="grainRowsAfterHierarchyFilter"
      :filteredEntities="grainRowsAfterTagFilter"
      :tagGroups="chartTagGroups"
      :groupsMode="infoTagGroupsMode"
      :infoTagLookup="infoTagLookup"
    />
  </div>
</template>

<script>
import axios from 'axios'
import { exportToExcel } from '@/utils/exportArraytoXlsx'
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import GroupedInfoTagFilter from '@/components/widgetcomponents/Dropdown_Selector/GroupedInfoTagFilter.vue'
import MultiSelectDropdown from '@/components/widgetcomponents/library/MultiSelectDropdown.vue'
import EntityReporting from '@/components/ConfigurablePageComponents/Engage/EntityReporting.vue'
import CC_Engagement_Score_Badge from '@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Engagement_Score_Badge.vue'
import demoProtectedFields from '@/mixins/demoProtectedFields'
import { entitySpoofKind } from '@/utils/demoSpoof'

const UNTAGGED_VALUE = '(none)'
const EXCLUDED_ENTITY_TYPES = new Set(['Patient', 'Staff'])
const SCOREABLE_ENTITY_TYPES = new Set(['Practice'])

const DEFAULT_PREFERRED_GROUP_BY = [
  'entity:Practice',
  'entity:Pods',
  'entity:Regions',
  'entity:Contract Pool',
  'entity:Contract (Cohort)',
  'entity:Contract',
  'entity:Payer',
  'entity:Line of Business',
  'entity:Market',
]

export default {
  name: 'Practice_Tag_Summary',
  components: {
    ExportMenu,
    GroupedInfoTagFilter,
    MultiSelectDropdown,
    EntityReporting,
    CC_Engagement_Score_Badge,
  },
  mixins: [demoProtectedFields],
  // The search filters practice rows whose names are tagged, so the typed term
  // identifies real practices. Demo Mode empties it and the tagged input is
  // read-only. It is not persisted.
  demoProtectedFields: ['searchQuery'],
  props: {
    preferredGroupBy: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: true,
      attrLoading: false,
      exporting: false,
      entities: [],
      entityMap: {},
      parentMap: {},
      childMap: {},
      allEntityTypeOptions: [],
      groupBy: ['entity:Practice'],
      activeFilterTypes: [],
      hierarchyFilters: {},
      infoTagGroups: [],
      infoTagGroupsMode: 'or',
      filterTagCache: {},
      selectedTagType: '__all__',
      searchQuery: '',
      sortColumn: 'name',
      sortDirection: 'asc',
      tagDisplayLimit: 6,
      attributionRows: [],
      breakdownRows: [],
      entityTotalsMap: {},
    }
  },
  computed: {
    entityTypeChips() {
      return this.allEntityTypeOptions
    },
    preferredTokens() {
      return (Array.isArray(this.preferredGroupBy) && this.preferredGroupBy.length) ? this.preferredGroupBy : DEFAULT_PREFERRED_GROUP_BY
    },
    entityChipsInline() {
      return this.entityTypeChips.filter(o => this.isInlineChip('entity:' + o.value))
    },
    dropdownEntityChips() {
      return this.entityTypeChips.filter(o => !this.isInlineChip('entity:' + o.value))
    },
    hasHiddenChips() {
      return this.dropdownEntityChips.length > 0
    },
    grainChip() {
      return this.groupBy.find(c => c.startsWith('entity:')) || null
    },
    grainType() {
      const chip = this.grainChip
      return chip ? chip.slice('entity:'.length) : null
    },
    columnChips() {
      return this.groupBy.filter(c => c !== this.grainChip)
    },
    columnDefs() {
      return this.columnChips.map(chip => {
        const type = chip.slice('entity:'.length)
        return { key: 'anc:' + type, label: type, kind: 'entity', dim: type }
      })
    },
    breakdownTypeList() {
      return this.columnDefs.map(c => c.dim)
    },
    breakdownSig() {
      return this.breakdownTypeList.join(',')
    },
    grainLabel() {
      return this.grainType || 'All Entities'
    },
    rowNoun() {
      if (this.columnDefs.length) return this.totalRows === 1 ? 'row' : 'rows'
      if (!this.grainType) return 'group' + (this.totalRows === 1 ? '' : 's')
      const base = this.grainType
      return (base.endsWith('s') ? base : base + 's')
    },
    noneLabel() {
      return UNTAGGED_VALUE
    },
    grainRows() {
      if (!this.grainType) return []
      return this.entities
        .filter(e => e.entity_type === this.grainType)
        .map(e => {
          const relatedByType = this.relatedDimensionsFor(e.id)
          const ancestorIds = new Set()
          for (const items of Object.values(relatedByType)) {
            for (const i of items) ancestorIds.add(i.id)
          }
          return {
            entity_id: e.id,
            name: e.name,
            tags: e.tags || [],
            relatedByType,
            ancestor_ids: ancestorIds,
            attributed_lives: this.entityTotalsMap[e.id] || 0,
            engagement_composite: e.engagement_score ? e.engagement_score.composite : null,
            last_engagement_date: this.practiceRollup(e.id).last,
          }
        })
    },
    grainRowsAfterHierarchyFilter() {
      let result = this.grainRows
      if (this.hasActiveHierarchyFilters) {
        result = result.filter(r =>
          Object.values(this.hierarchyFilters).every(ids =>
            ids.length === 0 || ids.some(id => r.ancestor_ids.has(id) || r.entity_id === id)
          )
        )
      }
      return result
    },
    grainRowsAfterTagFilter() {
      if (!this.hasActiveTagFilter) return this.grainRowsAfterHierarchyFilter
      const groups = this.activeTagGroups
      const groupsMode = this.infoTagGroupsMode === 'and' ? 'and' : 'or'
      return this.grainRowsAfterHierarchyFilter.filter(r => {
        const tagIds = new Set((r.tags || []).map(t => t.id))
        const groupResults = groups.map(g => {
          const include = g.include || []
          const exclude = g.exclude || []
          const mode = g.mode === 'and' ? 'and' : 'or'
          const incOk = include.length === 0
            ? true
            : (mode === 'and' ? include.every(tid => tagIds.has(tid)) : include.some(tid => tagIds.has(tid)))
          const excOk = !exclude.some(tid => tagIds.has(tid))
          return incOk && excOk
        })
        return groupsMode === 'and' ? groupResults.every(Boolean) : groupResults.some(Boolean)
      })
    },
    filteredGrainIds() {
      if (!this.grainType) return null
      if (!this.hasActiveHierarchyFilters && !this.hasActiveTagFilter) return null
      return new Set(this.grainRowsAfterTagFilter.map(r => r.entity_id))
    },
    availableTagTypes() {
      const types = new Set()
      for (const e of this.entities) {
        if (this.grainType && e.entity_type !== this.grainType) continue
        for (const t of (e.tags || [])) if (t.type) types.add(t.type)
      }
      return [...types].sort()
    },
    hierarchyFilterOptions() {
      const seen = {}
      for (const r of this.grainRows) {
        for (const [type, items] of Object.entries(r.relatedByType || {})) {
          if (!seen[type]) seen[type] = new Map()
          for (const item of items) {
            if (!seen[type].has(item.id)) {
              seen[type].set(item.id, { value: item.id, label: item.name })
            }
          }
        }
      }
      if (this.grainType) {
        const grainOpts = new Map()
        for (const r of this.grainRows) {
          if (!grainOpts.has(r.entity_id)) {
            grainOpts.set(r.entity_id, { value: r.entity_id, label: r.name })
          }
        }
        seen[this.grainType] = grainOpts
      }
      const result = {}
      for (const [type, m] of Object.entries(seen)) {
        const opts = [...m.values()].sort((a, b) => a.label.localeCompare(b.label))
        if (opts.length > 1) result[type] = opts
      }
      return result
    },
    filterDimensionTypes() {
      return Object.keys(this.hierarchyFilterOptions).sort()
    },
    availableFilterTypes() {
      return this.filterDimensionTypes.filter(t => !this.activeFilterTypes.includes(t))
    },
    hasActiveHierarchyFilters() {
      return Object.values(this.hierarchyFilters).some(ids => ids.length > 0)
    },
    activeTagGroups() {
      return (this.infoTagGroups || []).filter(g =>
        (g.include?.length || 0) > 0 || (g.exclude?.length || 0) > 0
      )
    },
    hasActiveTagFilter() {
      return this.activeTagGroups.length > 0
    },
    chartTagGroups() {
      if (this.activeTagGroups.length > 0) return this.activeTagGroups
      if (this.selectedTagType !== '__all__') {
        const ids = Object.values(this.infoTagLookup)
          .filter(t => t.type === this.selectedTagType)
          .map(t => t.id)
        if (ids.length > 0) return [{ include: ids, exclude: [], mode: 'or' }]
      }
      return []
    },
    hasActiveFilters() {
      return this.hasActiveHierarchyFilters || this.hasActiveTagFilter || !!this.searchQuery || this.activeFilterTypes.length > 0
    },
    infoTagLookup() {
      const lookup = { ...this.filterTagCache }
      for (const entity of this.entities) {
        for (const tag of (entity.tags || [])) {
          if (tag.id) {
            lookup[tag.id] = { id: tag.id, name: tag.name, type: tag.type, color: tag.color }
          }
        }
      }
      return lookup
    },
    activeTagTypes() {
      const set = new Set()
      if (this.selectedTagType !== '__all__') set.add(this.selectedTagType)
      for (const g of this.infoTagGroups || []) {
        const ids = [...(g.include || []), ...(g.exclude || [])]
        for (const id of ids) {
          const t = this.infoTagLookup[id]?.type
          if (t) set.add(t)
        }
      }
      return [...set]
    },
    showEngagementScore() {
      return !!this.grainType && SCOREABLE_ENTITY_TYPES.has(this.grainType)
    },
    displayColumns() {
      const cols = [{ key: 'name', label: this.grainLabel, sortable: true, align: 'left' }]
      for (const col of this.columnDefs) {
        cols.push({ key: col.key, label: col.label, sortable: true, align: 'left' })
      }
      cols.push({ key: 'tags', label: 'Tags', sortable: false, align: 'left' })
      if (this.showEngagementScore) {
        cols.push({ key: 'engagement_score', label: 'Engagement Score', sortable: true, align: 'left' })
      }
      cols.push({ key: 'engagements', label: 'Engagements', sortable: true, align: 'right' })
      cols.push({ key: 'attributed', label: 'Attributed Lives', sortable: true, align: 'right' })
      cols.push({ key: 'last', label: 'Last Engagement', sortable: true, align: 'right' })
      return cols
    },
    tableRows() {
      const allowed = this.filteredGrainIds
      const q = (this.searchQuery || '').toLowerCase()
      const cols = this.columnDefs
      const rows = []
      const source = cols.length ? this.breakdownRows : this.attributionRows
      for (const [ri, r] of source.entries()) {
        const entityId = r.entity_id
        if (allowed && entityId > 0 && !allowed.has(entityId)) continue
        const name = r.entity_name || ''
        if (q && !name.toLowerCase().includes(q)) continue
        const rollup = this.practiceRollup(entityId)
        const tags = this.entityMap[entityId] ? (this.entityMap[entityId].tags || []) : []
        const columns = {}
        const idParts = []
        cols.forEach((col, i) => {
          const value = r['bd' + i + '_name']
          columns[col.key] = (value == null || value === '') ? UNTAGGED_VALUE : value
          idParts.push(r['bd' + i + '_id'] == null ? 'none' : r['bd' + i + '_id'])
        })
        rows.push({
          key: `${ri}::${entityId}::${idParts.join('|')}`,
          entity_id: entityId,
          name,
          columns,
          attribution: r.attribution || 0,
          engagements: rollup.count,
          engagement_score: this.entityMap[entityId] ? (this.entityMap[entityId].engagement_score || null) : null,
          lastEngagement: rollup.last,
          tags,
        })
      }
      const dir = this.sortDirection === 'asc' ? 1 : -1
      return rows.sort((a, b) => {
        const va = this.getSortValue(a)
        const vb = this.getSortValue(b)
        if (va != null || vb != null) {
          if (va == null) return 1
          if (vb == null) return -1
          const cmp = typeof va === 'string' ? va.localeCompare(vb) : (va - vb)
          if (cmp !== 0) return dir * cmp
        }
        const aNone = this.columnDefs.some(c => a.columns[c.key] === UNTAGGED_VALUE) ? 1 : 0
        const bNone = this.columnDefs.some(c => b.columns[c.key] === UNTAGGED_VALUE) ? 1 : 0
        return (a.name || '').localeCompare(b.name || '')
          || (aNone - bNone)
          || (b.attribution - a.attribution)
          || this.columnDefs.map(c => a.columns[c.key]).join('|~|').localeCompare(this.columnDefs.map(c => b.columns[c.key]).join('|~|'))
      })
    },
    totalRows() {
      return this.tableRows.length
    },
  },
  watch: {
    grainType() {
      this.activeFilterTypes = []
      this.hierarchyFilters = {}
      this.selectedTagType = '__all__'
      this.infoTagGroups = []
      this._relCache = {}
      this.queueLoadAttribution()
    },
    breakdownSig() {
      this.queueLoadAttribution()
    },
  },
  async mounted() {
    await this.fetchEntityTypes()
    await Promise.all([this.fetchData(), this.fetchAllInfoTags()])
    await this.loadAttribution()
  },
  beforeUnmount() {
    clearTimeout(this._attrDebounce)
  },
  methods: {
    entitySpoofKind,
    formatNum(v) {
      return (v == null ? 0 : Number(v)).toLocaleString()
    },
    formatDate(dateStr) {
      if (!dateStr) return '—'
      const d = new Date(dateStr)
      if (isNaN(d)) return '—'
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
      const days = Math.floor((new Date() - d) / 86400000)
      return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} (${days}d)`
    },
    isSelected(chip) {
      return this.groupBy.includes(chip)
    },
    orderNum(chip) {
      return this.groupBy.indexOf(chip) + 1
    },
    toggleChip(chip) {
      this.groupBy = this.groupBy.includes(chip)
        ? this.groupBy.filter(c => c !== chip)
        : [...this.groupBy, chip]
    },
    isInlineChip(chip) {
      return this.preferredTokens.includes(chip) || this.groupBy.includes(chip)
    },
    onAddGroupBy(event) {
      const value = event.target.value
      if (value && !this.groupBy.includes(value)) this.groupBy = [...this.groupBy, value]
      event.target.value = ''
    },
    chipClass(active) {
      return active
        ? 'px-2.5 py-1 text-xs rounded-full border border-primary-500 bg-primary-50 text-primary-700 font-medium'
        : 'px-2.5 py-1 text-xs rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
    },
    async fetchData() {
      this.loading = true
      try {
        const params = {}
        const types = this.allEntityTypeOptions.map(o => o.value).filter(Boolean)
        if (types.length > 0) params.entity_types = types.join(',')
        const response = await axios.get('/api/entities/entity_tree/', { params })
        const byId = {}
        const parentMap = {}
        for (const node of response.data || []) {
          if (!byId[node.id]) {
            const { parentId, ...rest } = node
            byId[node.id] = rest
          }
          if (node.parentId) {
            if (!parentMap[node.id]) parentMap[node.id] = []
            if (!parentMap[node.id].includes(node.parentId)) parentMap[node.id].push(node.parentId)
          }
        }
        const childMap = {}
        for (const [childId, parents] of Object.entries(parentMap)) {
          for (const pid of parents) {
            if (!childMap[pid]) childMap[pid] = []
            if (!childMap[pid].includes(Number(childId))) childMap[pid].push(Number(childId))
          }
        }
        this.entityMap = byId
        this.parentMap = parentMap
        this.childMap = childMap
        this._descCache = {}
        this._ancCache = {}
        this._relCache = {}
        this._rollupCache = {}
        this.entities = Object.values(byId)
      } finally {
        this.loading = false
      }
    },
    async fetchEntityTypes() {
      const response = await axios.get('/api/entity-types/', { params: { include_counts: true } })
      const opts = (response.data || [])
        .filter(t => t.is_active && !EXCLUDED_ENTITY_TYPES.has(t.type_name))
        .map(t => ({ value: t.type_name, label: t.type_name }))
        .sort((a, b) => a.label.localeCompare(b.label))
      const practiceIdx = opts.findIndex(o => o.value === 'Practice')
      if (practiceIdx > 0) {
        const [practice] = opts.splice(practiceIdx, 1)
        opts.unshift(practice)
      }
      this.allEntityTypeOptions = opts
      if (this.grainType && !opts.find(o => o.value === this.grainType)) {
        const fallback = this.entityTypeChips[0] || opts[0]
        this.groupBy = fallback ? ['entity:' + fallback.value] : []
      }
    },
    async fetchAllInfoTags() {
      const [tagsRes, typesRes] = await Promise.all([
        axios.get('/api/infotags/'),
        axios.get('/api/infotagtypes/'),
      ])
      const typeNameMap = {}
      for (const t of (typesRes.data || [])) typeNameMap[t.id] = t.name
      const cache = {}
      for (const tag of (tagsRes.data || [])) {
        cache[tag.id] = { id: tag.id, name: tag.name, type: typeNameMap[tag.infoTagType] || 'Other', color: null }
      }
      this.filterTagCache = cache
    },
    queueLoadAttribution() {
      clearTimeout(this._attrDebounce)
      this._attrDebounce = setTimeout(() => this.loadAttribution(), 250)
    },
    async loadAttribution() {
      const token = (this._attrToken || 0) + 1
      this._attrToken = token
      this.attrLoading = true
      try {
        const grainType = this.grainType
        const breakdownTypes = this.breakdownTypeList
        const report = grainType
          ? await this.fetchBreakdown(grainType, [])
          : await this.fetchAttributionReport()
        if (this._attrToken !== token) return
        this.attributionRows = report.results || []
        this.entityTotalsMap = this.totalsFromResults(this.attributionRows)
        if (grainType && breakdownTypes.length) {
          const breakdown = await this.fetchBreakdown(grainType, breakdownTypes)
          if (this._attrToken !== token) return
          this.breakdownRows = breakdown.results || []
        } else {
          this.breakdownRows = []
        }
      } finally {
        if (this._attrToken === token) this.attrLoading = false
      }
    },
    async fetchAttributionReport() {
      const params = {}
      if (this.grainType) params.entity_types = this.grainType
      const response = await axios.get('/api/entities/attribution-report/', { params })
      return response.data || { results: [] }
    },
    async fetchBreakdown(grainType, breakdownTypes) {
      const params = { grain_type: grainType, breakdown_types: breakdownTypes.join(',') }
      const response = await axios.get('/api/entities/attribution-breakdown/', { params })
      return response.data || { results: [] }
    },
    totalsFromResults(results) {
      const map = {}
      for (const r of results) map[r.entity_id] = r.attribution || 0
      return map
    },
    descendantsOf(entityId) {
      if (!this._descCache) this._descCache = {}
      if (this._descCache[entityId]) return this._descCache[entityId]
      const set = new Set()
      const queue = [entityId]
      while (queue.length) {
        const id = queue.shift()
        if (set.has(id)) continue
        set.add(id)
        for (const c of (this.childMap[id] || [])) queue.push(c)
      }
      this._descCache[entityId] = set
      return set
    },
    walkAncestors(entityId) {
      if (!this._ancCache) this._ancCache = {}
      if (this._ancCache[entityId]) return this._ancCache[entityId]
      const ancestors = []
      const visited = new Set([entityId])
      const queue = [...(this.parentMap[entityId] || [])]
      while (queue.length) {
        const pid = queue.shift()
        if (visited.has(pid)) continue
        visited.add(pid)
        const parent = this.entityMap[pid]
        if (!parent) continue
        ancestors.push(parent)
        if (this.parentMap[pid]) queue.push(...this.parentMap[pid])
      }
      this._ancCache[entityId] = ancestors
      return ancestors
    },
    relatedDimensionsFor(entityId) {
      if (!this._relCache) this._relCache = {}
      if (this._relCache[entityId]) return this._relCache[entityId]
      const result = {}
      const add = (typeName, id, name) => {
        if (!typeName || typeName === this.grainType) return
        if (id === entityId) return
        if (!result[typeName]) result[typeName] = []
        if (!result[typeName].find(x => x.id === id)) result[typeName].push({ id, name })
      }
      const visited = new Set([entityId])
      const subtree = [entityId, ...this.descendantsOf(entityId)]
      for (const nodeId of subtree) {
        for (const a of this.walkAncestors(nodeId)) {
          if (visited.has(a.id)) continue
          visited.add(a.id)
          add(a.entity_type, a.id, a.name)
        }
      }
      this._relCache[entityId] = result
      return result
    },
    practiceRollup(entityId) {
      if (!this._rollupCache) this._rollupCache = {}
      if (entityId in this._rollupCache) return this._rollupCache[entityId]
      let count = 0
      let last = null
      const nodeIds = entityId > 0
        ? this.descendantsOf(entityId)
        : Object.keys(this.entityMap).map(Number)
      for (const id of nodeIds) {
        const n = this.entityMap[id]
        if (!n || !SCOREABLE_ENTITY_TYPES.has(n.entity_type)) continue
        count += n.num_engagements || 0
        if (n.last_engagement_date) {
          if (!last || new Date(n.last_engagement_date) > new Date(last)) last = n.last_engagement_date
        }
      }
      const result = { count, last }
      this._rollupCache[entityId] = result
      return result
    },
    onAddFilterType(event) {
      const value = event.target.value
      if (!value) return
      if (!this.activeFilterTypes.includes(value)) {
        this.activeFilterTypes = [...this.activeFilterTypes, value]
      }
      event.target.value = ''
    },
    removeFilterType(typeName) {
      this.activeFilterTypes = this.activeFilterTypes.filter(t => t !== typeName)
      const next = { ...this.hierarchyFilters }
      delete next[typeName]
      this.hierarchyFilters = next
    },
    onTagTypeChange() {
      this.infoTagGroups = []
    },
    updateHierarchyFilter(typeName, selectedIds) {
      this.hierarchyFilters = { ...this.hierarchyFilters, [typeName]: selectedIds }
    },
    clearFilters() {
      this.activeFilterTypes = []
      this.hierarchyFilters = {}
      this.infoTagGroups = []
      this.selectedTagType = '__all__'
      this.searchQuery = ''
    },
    toggleSort(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortColumn = column
        this.sortDirection = 'asc'
      }
    },
    sortIndicator(column) {
      if (this.sortColumn !== column) return ''
      return this.sortDirection === 'asc' ? '▲' : '▼'
    },
    getSortValue(item) {
      const col = this.sortColumn
      if (col && col.startsWith('anc:')) return item.columns[col] || ''
      switch (col) {
        case 'name': return item.name || ''
        case 'engagements': return item.engagements
        case 'engagement_score': return item.engagement_score ? item.engagement_score.composite : null
        case 'attributed': return item.attribution
        case 'last': return item.lastEngagement || null
        default: return item.name || ''
      }
    },
    visibleTagsFor(item) {
      const all = item.tags || []
      const types = this.activeTagTypes
      return types.length === 0 ? all : all.filter(t => types.includes(t.type))
    },
    displayTags(item) {
      return this.visibleTagsFor(item).slice(0, this.tagDisplayLimit)
    },
    tagOverflowCount(item) {
      return Math.max(0, this.visibleTagsFor(item).length - this.tagDisplayLimit)
    },
    overflowTagsTooltip(item) {
      const overflow = this.visibleTagsFor(item).slice(this.tagDisplayLimit)
      return overflow.map(t => t.type ? `${t.type}: ${t.name}` : t.name).join('\n')
    },
    goToEntity(item) {
      if (item.entity_id > 0) this.$router.push(`/entity/${item.entity_id}`)
    },
    async handleExport() {
      const rows = this.tableRows
      if (!rows.length) return
      this.exporting = true
      try {
        const data = rows.map(item => {
          const out = {}
          out[this.grainLabel] = item.name
          for (const col of this.columnDefs) out[col.label] = item.columns[col.key] || ''
          out['Tags'] = (item.tags || []).map(t => t.name).join(', ')
          if (this.showEngagementScore) out['Engagement Score'] = item.engagement_score ? item.engagement_score.composite : ''
          out['Engagements'] = item.engagements
          out['Attributed Lives'] = item.attribution
          out['Last Engagement'] = item.lastEngagement || ''
          return out
        })
        const slug = (this.grainType || 'all').toLowerCase().replace(/\s+/g, '_')
        await exportToExcel(data, `entity_summary_${slug}.xlsx`)
      } finally {
        this.exporting = false
      }
    },
  },
}
</script>
